import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Trash, Check, ArrowsClockwise, Star, X } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { isBabyPatient } from '@/lib/utils';
import ShareButton from '@/components/ShareButton';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  horticolas: 'Hortícolas e Vegetais',
  frutas: 'Frutas',
  proteinas: 'Proteínas',
  laticinios: 'Laticínios',
  cereais: 'Cereais e Grãos',
  gorduras: 'Gorduras',
  outros: 'Outros',
};
const CATEGORY_ORDER = ['horticolas', 'frutas', 'proteinas', 'laticinios', 'cereais', 'gorduras', 'outros'];

export default function ComprasPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [hasPlan, setHasPlan] = useState(false);
  const [hasMenu, setHasMenu] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [newFavorite, setNewFavorite] = useState('');
  const [newFavCategory, setNewFavCategory] = useState('outros');

  const isBaby = isBabyPatient(user);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/my-shopping-list`);
      setList(data?.items?.length > 0 || data?.manual_items?.length > 0 ? data : null);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/shopping-favorites`); setFavorites(data || []); } catch {}
  }, []);

  const checkSources = useCallback(async () => {
    try {
      const [planRes, menuRes] = await Promise.all([
        axios.get(`${API}/api/meal-plans`).catch(() => ({ data: [] })),
        isBaby
          ? axios.get(`${API}/api/my-baby-menus`).catch(() => ({ data: [] }))
          : axios.get(`${API}/api/menus`).catch(() => ({ data: [] })),
      ]);
      setHasPlan(Array.isArray(planRes.data) && planRes.data.some(p => p.status === 'ativo'));
      setHasMenu(Array.isArray(menuRes.data) && menuRes.data.length > 0);
    } catch {}
  }, [isBaby]);

  useEffect(() => { fetchList(); checkSources(); fetchFavorites(); }, [fetchList, checkSources, fetchFavorites]);

  const generate = async (sources) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/shopping-list/generate`, { sources });
      setList(data);
      setShowSourcePicker(false);
      toast.success('Lista gerada!');
    } catch { toast.error('Erro ao gerar lista'); }
    finally { setLoading(false); }
  };

  const toggleItem = async (idx) => {
    try { const { data } = await axios.put(`${API}/api/my-shopping-list/toggle`, { index: idx }); setList(data); } catch {}
  };
  const addManualItem = async (name, category) => {
    try { const { data } = await axios.post(`${API}/api/my-shopping-list/manual-item`, { name, category }); setList(data); } catch { toast.error('Erro ao adicionar'); }
  };
  const addManual = async () => {
    if (!newItem.trim()) return;
    await addManualItem(newItem.trim(), 'outros');
    setNewItem('');
  };
  const toggleManual = async (idx) => {
    try { const { data } = await axios.put(`${API}/api/my-shopping-list/manual-item/toggle`, { index: idx }); setList(data); } catch {}
  };
  const removeManual = async (idx) => {
    const item = (list.manual_items || [])[idx];
    const ok = await confirmDelete({ description: `Vai apagar o item "${item?.name || ''}" da lista de compras.` });
    if (!ok) return;
    try { const { data } = await axios.delete(`${API}/api/my-shopping-list/manual-item/${idx}`); setList(data); } catch {}
  };
  const addFavorite = async () => {
    if (!newFavorite.trim()) return;
    try { const { data } = await axios.post(`${API}/api/shopping-favorites`, { name: newFavorite.trim(), category: newFavCategory }); setFavorites(data); setNewFavorite(''); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao adicionar favorito'); }
  };
  const removeFavorite = async (idx) => {
    const fav = favorites[idx];
    const ok = await confirmDelete({ description: `Vai apagar o favorito "${fav?.name || ''}".` });
    if (!ok) return;
    try { const { data } = await axios.delete(`${API}/api/shopping-favorites/${idx}`); setFavorites(data); } catch {}
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  // No list yet — show source picker
  if (!list) {
    return (
      <div className="fade-in" data-testid="compras-page">
        <div className="text-center py-12">
          <ShoppingCart size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <h2 className="font-heading font-medium text-xl text-brand-text mb-2">Lista de Compras</h2>
          <p className="text-sm text-brand-muted mb-6">Gera a tua lista com base no plano ou ementa.</p>
          <SourcePicker hasPlan={hasPlan} hasMenu={hasMenu} isBaby={isBaby} onGenerate={generate} />
        </div>
        <FavoritesSection favorites={favorites} newFavorite={newFavorite} setNewFavorite={setNewFavorite} newCategory={newFavCategory} setNewCategory={setNewFavCategory} onAdd={addFavorite} onRemove={removeFavorite} onAddToList={addManualItem} />
      </div>
    );
  }

  // Group items by category (itens de receitas + manuais/favoritos, tudo junto)
  const grouped = {};
  (list.items || []).forEach((item, idx) => {
    const cat = item.category || 'outros';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ ...item, _idx: idx, _source: 'auto' });
  });
  (list.manual_items || []).forEach((item, idx) => {
    const cat = item.category || 'outros';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ ...item, _idx: idx, _source: 'manual' });
  });
  const checkedCount = (list.items || []).filter(i => i.checked).length + (list.manual_items || []).filter(i => i.checked).length;
  const totalCount = (list.items || []).length + (list.manual_items || []).length;

  return (
    <div className="fade-in" data-testid="compras-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Lista de Compras</h2>
          <p className="text-xs text-brand-muted mt-0.5">{checkedCount} de {totalCount} itens</p>
        </div>
        <div className="flex items-center gap-1">
          <ShareButton resources={{ current_shopping: true }} label="Lista de compras" />
          <button onClick={() => setShowSourcePicker(true)} className="p-2 text-brand-green hover:bg-brand-green/10 rounded-full" title="Regenerar lista" data-testid="regenerate-list-btn">
            <ArrowsClockwise size={20} />
          </button>
        </div>
      </div>

      {showSourcePicker && (
        <div className="mb-4 bg-white border border-brand-border rounded-2xl p-4">
          <SourcePicker hasPlan={hasPlan} hasMenu={hasMenu} isBaby={isBaby} onGenerate={generate} />
        </div>
      )}

      {/* Items by category (receitas + manuais + favoritos, tudo junto) */}
      <div className="space-y-4 mb-4">
        {CATEGORY_ORDER.map(cat => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          return (
            <div key={cat}>
              <h3 className="text-xs font-semibold text-brand-green uppercase tracking-wider mb-2">{CATEGORY_LABELS[cat] || cat}</h3>
              <div className="space-y-1">
                {items.map(item => (
                  <div key={`${item._source}-${item._idx}`} className="flex items-center gap-2">
                    <button
                      onClick={() => item._source === 'manual' ? toggleManual(item._idx) : toggleItem(item._idx)}
                      className={`flex-1 text-left flex items-center gap-3 bg-white border border-brand-border rounded-xl px-3 py-2.5 ${item.checked ? 'opacity-50' : ''}`}
                      data-testid={`item-${item._source}-${item._idx}`}
                    >
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${item.checked ? 'bg-brand-green border-brand-green' : 'border-brand-border'}`}>
                        {item.checked && <Check size={12} className="text-white" />}
                      </div>
                      <span className={`text-sm flex-1 ${item.checked ? 'line-through text-brand-muted' : 'text-brand-text'}`}>{item.name}</span>
                      {item.quantity && <span className="text-[10px] text-brand-muted flex-shrink-0">{item.quantity}</span>}
                    </button>
                    {item._source === 'manual' && (
                      <button onClick={() => removeManual(item._idx)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`remove-manual-${item._idx}`}><Trash size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add manual item */}
      <div className="flex gap-2 mb-6">
        <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Adicionar item..." className="rounded-xl border-brand-border flex-1" onKeyDown={e => { if (e.key === 'Enter') addManual(); }} data-testid="add-manual-item" />
        <button onClick={addManual} className="bg-brand-green text-white rounded-xl px-4 py-2"><Plus size={16} /></button>
      </div>

      <FavoritesSection favorites={favorites} newFavorite={newFavorite} setNewFavorite={setNewFavorite} newCategory={newFavCategory} setNewCategory={setNewFavCategory} onAdd={addFavorite} onRemove={removeFavorite} onAddToList={addManualItem} />
    </div>
  );
}

const FAV_CATEGORY_OPTIONS = [
  { v: 'horticolas', l: 'Hortícolas' }, { v: 'frutas', l: 'Frutas' }, { v: 'proteinas', l: 'Proteínas' },
  { v: 'laticinios', l: 'Laticínios' }, { v: 'cereais', l: 'Cereais' }, { v: 'gorduras', l: 'Gorduras' }, { v: 'outros', l: 'Outros' },
];

function FavoritesSection({ favorites, newFavorite, setNewFavorite, newCategory, setNewCategory, onAdd, onRemove, onAddToList }) {
  return (
    <div className="mt-2 bg-white border border-brand-border rounded-2xl p-4" data-testid="favorites-section">
      <h3 className="text-xs font-semibold text-brand-green uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
        <Star size={14} weight="fill" /> Favoritos
      </h3>
      <p className="text-xs text-brand-muted mb-3">Produtos que compras sempre, adiciona à lista da semana com um toque, sem escrever de novo.</p>
      {favorites.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {favorites.map((fav, idx) => (
            <span key={idx} className="flex items-center gap-1.5 bg-brand-mint/15 border border-brand-mint/30 text-sm text-brand-text pl-3 pr-1.5 py-1.5 rounded-full" data-testid={`favorite-chip-${idx}`}>
              {fav.name}
              <span className="text-[9px] text-brand-muted bg-white/60 px-1.5 py-0.5 rounded-full">{CATEGORY_LABELS[fav.category] || 'Outros'}</span>
              <button onClick={() => onAddToList(fav.name, fav.category)} className="p-1 rounded-full hover:bg-brand-green/20 text-brand-green" title="Adicionar à lista" data-testid={`favorite-add-to-list-${idx}`}>
                <Plus size={12} weight="bold" />
              </button>
              <button onClick={() => onRemove(idx)} className="p-1 rounded-full hover:bg-red-100 text-brand-placeholder hover:text-red-500" title="Remover favorito" data-testid={`favorite-remove-${idx}`}>
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={newFavorite} onChange={e => setNewFavorite(e.target.value)} placeholder="Novo favorito (ex: Leite, Ovos...)" className="rounded-xl border-brand-border flex-1 text-sm" onKeyDown={e => { if (e.key === 'Enter') onAdd(); }} data-testid="add-favorite-input" />
        <select value={newCategory} onChange={e => setNewCategory(e.target.value)} className="rounded-xl border border-brand-border text-sm px-2.5 bg-white text-brand-text" data-testid="add-favorite-category-select">
          {FAV_CATEGORY_OPTIONS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <button onClick={onAdd} className="bg-brand-green/10 text-brand-green rounded-xl px-3.5 py-2" data-testid="add-favorite-btn"><Plus size={16} /></button>
      </div>
    </div>
  );
}

function SourcePicker({ hasPlan, hasMenu, isBaby, onGenerate }) {
  const [selected, setSelected] = useState([]);
  const toggle = (s) => setSelected(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  return (
    <div className="space-y-3">
      <p className="text-sm text-brand-text font-medium text-center">O que queres incluir na lista?</p>
      <div className="flex flex-col gap-2">
        {hasPlan && (
          <button onClick={() => toggle('plano')} className={`p-3 rounded-xl border text-left text-sm ${selected.includes('plano') ? 'border-brand-green bg-brand-green/5 text-brand-green font-medium' : 'border-brand-border text-brand-muted'}`} data-testid="source-plano">
            Plano alimentar
          </button>
        )}
        {hasMenu && (
          <button onClick={() => toggle('ementa')} className={`p-3 rounded-xl border text-left text-sm ${selected.includes('ementa') ? 'border-brand-green bg-brand-green/5 text-brand-green font-medium' : 'border-brand-border text-brand-muted'}`} data-testid="source-ementa">
            {isBaby ? 'Ementa do bebé' : 'Ementa semanal'}
          </button>
        )}
        {!hasPlan && !hasMenu && <p className="text-xs text-brand-muted text-center">Nenhum plano ou ementa ativo.</p>}
      </div>
      {selected.length > 0 && (
        <button onClick={() => onGenerate(selected)} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="generate-list-btn">
          Gerar lista de compras
        </button>
      )}
    </div>
  );
}
